/**
 * apiClient.ts
 *
 * Thin fetch wrapper for the Phase-2 FastAPI backend.
 *
 * Design rules:
 *   1. **Optional**. If the backend isn't reachable for the current
 *      workspace, callers detect that via `apiConfigured()` and fall
 *      back to the existing Supabase path. Turning the backend off
 *      doesn't break the app — exactly what we want during the cutover.
 *   2. **Auth bridge**. Forwards the current Supabase session JWT in the
 *      `Authorization: Bearer <token>` header. The backend's
 *      `app.core.auth` validates it against the Supabase JWKS, so the
 *      frontend never needs to know about backend-issued tokens during
 *      Phase-1.
 *   3. **No throwing on network errors** — returns `{ ok: false, error }`
 *      so the caller can decide whether to retry, fall back, or surface.
 *   4. **Per-workspace cutover gate (v7.38)**. Even when the env var is
 *      set, `getApiBase()` only resolves to a real URL if the current
 *      workspace's id is in the opt-in list stored under
 *      `localStorage[pulsehq_phase2_workspaces]`. This gives us a safe,
 *      per-workspace gradual rollout: no workspace hits FastAPI by
 *      default; flipping a workspace into the list re-routes only that
 *      workspace's bridged paths to the backend, others stay on
 *      Supabase. Rollback = `localStorage.removeItem(...)` in DevTools.
 *
 *   5. **The per-workspace gate STAYS HERE, and a per-route gate was added
 *      ON TOP of it in `apiBridge.ts` (v7.6x).** The fallback-contract
 *      proposal says this check should move OUT of `getApiBase()` so the
 *      function does one thing. It has not moved, on purpose: the check
 *      being here is the only reason the 112 raw `api.X()` calls that have
 *      NOT been converted to the bridge short-circuit at all. Moving it
 *      today would un-gate every one of them in the same commit that added
 *      a finer gate — the opposite of the intent. A route now needs BOTH
 *      gates to say yes, which is strictly more restrictive than before,
 *      never less. Move this the day the last raw call is converted, and
 *      not before; `src/lib/__tests__/apiBridgeGuard.test.ts` counts them
 *      down.
 *
 * Usage:
 *   import { api } from './apiClient';
 *   const res = await api.post('/notifications', payload);
 *   if (!res.ok) { fallbackToSupabase(); }
 */

import { supabase } from './supabase';

// ─── Per-workspace cutover gate (v7.38) ─────────────────────────────────────
//
// The two localStorage keys this file owns. Both are JSON or plain
// strings — no nested structure — so a human can edit them via DevTools
// in an emergency.

/** Opt-in list. Format: JSON array of workspace-id strings. */
const PHASE2_WORKSPACES_KEY = 'pulsehq_phase2_workspaces';

/** Current workspace id, written by AuthContext when profile loads. */
const CURRENT_WORKSPACE_KEY = 'pulsehq_current_workspace_id';

/**
 * The production base URL, RELATIVE on purpose.
 *
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║  ⛔ RELATIVE, SO IT MATCHES WHATEVER ORIGIN SERVED THE PAGE.              ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 * This was `http://109.199.116.126/api/v1` — plain HTTP at a bare IP — while the
 * site is served from `https://cbops.app` and `https://pulsehq-dev.duckdns.org`
 * as well as that address. One pinned origin cannot be right for three, and the
 * mismatch is the kind that works for whoever set it up and fails for everyone
 * else: a secure page calling an insecure address is mixed content, which a
 * browser can block outright, and a request that never leaves shows up as a
 * failure with no server-side trace at all.
 *
 * `/api/v1` has no origin, so it is always the origin the page came from. nginx
 * carves that prefix out on every server block (they share one snippet), so it
 * resolves for all three names without any of them being named here.
 *
 * ⚠️ WHAT THIS GIVES UP, STATED RATHER THAN DISCOVERED: a page served from an
 * origin that does NOT proxy `/api/v1` can no longer reach the backend. That is
 * the point — such a page reaching a hardcoded production IP was never something
 * to rely on — but it means a `file://` page or a third-party host needs
 * `REACT_APP_API_BASE_URL`, which is still honoured above and still wins.
 */
const PROD_API_BASE_URL = '/api/v1';

/** Defensive localStorage read — handles SSR / private mode / quota. */
function lsGet(key: string): string | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/** Defensive localStorage write — swallows errors silently. */
function lsSet(key: string, value: string): void {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(key, value);
  } catch {
    /* noop */
  }
}

/** Defensive localStorage delete — swallows errors silently. */
function lsRemove(key: string): void {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.removeItem(key);
  } catch {
    /* noop */
  }
}

/**
 * Read the opt-in list. Always returns an array (empty on missing /
 * malformed value) so callers don't need null checks.
 */
export function getPhase2Workspaces(): string[] {
  const raw = lsGet(PHASE2_WORKSPACES_KEY);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((x): x is string => typeof x === 'string' && x.length > 0);
  } catch {
    return [];
  }
}

/** Replace the entire opt-in list. */
export function setPhase2Workspaces(ids: string[]): void {
  // Dedupe + filter out empty/whitespace-only entries.
  const cleaned = Array.from(
    new Set(
      ids
        .map((id) => (typeof id === 'string' ? id.trim() : ''))
        .filter((id) => id.length > 0),
    ),
  );
  lsSet(PHASE2_WORKSPACES_KEY, JSON.stringify(cleaned));
}

/** Add one workspace to the opt-in list. Idempotent. */
export function addPhase2Workspace(id: string): void {
  if (typeof id !== 'string' || !id.trim()) return;
  const current = getPhase2Workspaces();
  if (current.includes(id.trim())) return;
  setPhase2Workspaces([...current, id.trim()]);
}

/** Remove one workspace from the opt-in list. */
export function removePhase2Workspace(id: string): void {
  setPhase2Workspaces(getPhase2Workspaces().filter((x) => x !== id));
}

/**
 * Read the current workspace id (set by AuthContext on profile load).
 *
 * THIS IS THE PLAIN-MODULE TWIN OF `useWorkspaceId()`. Anything under `src/lib/` is not a
 * React component and cannot call a hook, so it asks here instead. Both read the same value,
 * published by AuthContext from `profiles.workspace_id`.
 *
 * Returns `null` before the profile has resolved, and after logout. **A null must never be
 * defaulted to a workspace** — that is the bug this whole migration exists to remove. A read
 * with no workspace returns nothing; a write with no workspace does not happen. Use
 * `requireWorkspaceId()` for the write side.
 */
export function getCurrentWorkspaceId(): string | null {
  return lsGet(CURRENT_WORKSPACE_KEY);
}

/**
 * The message a person sees when something is attempted before their profile has loaded.
 * Matches the wording the migrated pages already use, so the app says one thing, not five.
 */
export const NO_WORKSPACE_MESSAGE = 'Still signing you in — try again in a moment.';

/**
 * The current workspace, or throw.
 *
 * For WRITE paths in plain modules. A write that cannot name its workspace must not reach the
 * database at all: the database would either refuse it (where a policy is workspace-scoped) or
 * — worse, on the handful of tables that still carry a literal default — silently file it
 * under company one. Failing here makes that impossible and gives the caller a sentence it can
 * show a person.
 *
 * The thrown Error carries `code = 'NO_WORKSPACE'`, matching the `fail()` convention used by
 * the service modules, so a caller that switches on error codes can tell this apart from a
 * PostgREST failure.
 */
export function requireWorkspaceId(): string {
  const ws = getCurrentWorkspaceId();
  if (ws) return ws;
  const err = new Error(NO_WORKSPACE_MESSAGE) as Error & { code?: string };
  err.code = 'NO_WORKSPACE';
  throw err;
}

/** Set/clear the current workspace id. Pass null on logout. */
export function setCurrentWorkspaceId(id: string | null): void {
  if (id) {
    lsSet(CURRENT_WORKSPACE_KEY, id);
  } else {
    lsRemove(CURRENT_WORKSPACE_KEY);
  }
}

/**
 * True iff the current workspace is opted in to Phase-2.
 * False otherwise (no current workspace, or workspace not in list).
 *
 * This is the single source of truth for "should api.X() reach the
 * backend right now". The admin UI in CompanySettingsPage uses it to
 * render a green/red status pill so the user can sanity-check before
 * navigating.
 */
export function isPhase2EnabledForCurrentWorkspace(): boolean {
  const ws = getCurrentWorkspaceId();
  if (!ws) return false;
  return getPhase2Workspaces().includes(ws);
}

// ─── Base URL resolution ────────────────────────────────────────────────────

/**
 * Resolve the API base URL for the CURRENT request.
 *
 * Re-evaluated on every call (instead of cached at module load) so:
 *   - Tests can flip `process.env.REACT_APP_API_BASE_URL` and the
 *     localStorage gate keys between cases without `jest.resetModules()`.
 *   - The admin UI's "Add my workspace" button takes effect on the very
 *     next api.X() call without an app reload.
 *
 * Two-layer gate:
 *   1. Per-workspace opt-in (v7.38). If the current workspace isn't in
 *      the opt-in list, return "" — every api.X() short-circuits to
 *      `{ ok: false, error: "Backend not configured" }` and the bridge
 *      pattern hands control back to Supabase. This is the safety net
 *      that lets us flip workspaces one at a time.
 *   2. URL source. Once the gate is open, prefer
 *      `REACT_APP_API_BASE_URL` (CRA build-time env, useful for staging
 *      / local backend); fall back to the hardcoded production URL so
 *      the test workspace flow works on the production frontend without
 *      requiring a rebuild + redeploy first.
 */
function getApiBase(): string {
  // SELF-HOSTED WINS OVER THE GATE. The per-workspace gate is a FALLBACK
  // SWITCH whose "off" position means *use Supabase* — see resolveApiUrl's
  // note below. On this branch there IS no Supabase to fall back to, so once
  // REACT_APP_API_BASE_URL is set, every workspace is ours and the gate has
  // nothing left to mean. Without this, a self-hosted deployment answers
  // "Backend not configured" on every screen until somebody edits localStorage.
  const selfHosted = (process.env.REACT_APP_API_BASE_URL ?? '').replace(/\/$/, '');
  if (selfHosted) return selfHosted;

  if (!isPhase2EnabledForCurrentWorkspace()) return '';
  return resolveApiUrl();
}

/**
 * The URL half of `getApiBase()`, WITHOUT the per-workspace gate.
 *
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║  ⚠️ THIS EXISTS FOR API-ONLY CALLS, AND FOR NOTHING ELSE.                ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 * *Added 2026-08-28, on Lis's ruling, while building the mail settings screen.*
 *
 * The per-workspace gate is a FALLBACK SWITCH: "off" means *use Supabase*. That is
 * the right shape for the 112 raw `api.X()` calls and for every bridged route,
 * because each of them has a Supabase path behind it.
 *
 * **It is the wrong shape where there is no Supabase path.** Connecting a mailbox
 * writes to `mail_account` through a FastAPI route that holds the credential in
 * Vault; there is no Supabase equivalent and there never will be. So for those
 * calls "gate off" does not mean *fall back* — it means *the screen does nothing*,
 * silently, on a workspace nobody remembered to add to a localStorage array.
 *
 * ⛔ **AND NOTHING IN THE PRODUCT CAN ADD ONE.** `setPhase2Workspaces` and
 *    `addPhase2Workspace` are exported above and **no component calls them**
 *    (measured 2026-08-28). The "admin UI's Add my workspace button" named in the
 *    comment on `getApiBase()` does not exist. The list is DevTools-only.
 *
 * ⛔ **THE GATE IS NOT HOISTED OUT OF `getApiBase()`, DELIBERATELY.** The header of
 *    this file says why: it is the only reason those 112 unconverted raw calls
 *    short-circuit at all, and moving it would un-gate every one of them in the same
 *    commit. This function sits BESIDE the gate rather than removing it.
 *
 * So: use this only through `apiOnlyRead` / `apiOnlyWrite` in `apiBridge.ts`. A
 * caller reaching for it directly is almost certainly reaching for `api.X()` and
 * should say so.
 */
export function resolveApiUrl(): string {
  const fromEnv = (process.env.REACT_APP_API_BASE_URL ?? '').replace(/\/$/, '');
  if (fromEnv) return fromEnv;
  // Self-hosted mode is configured purely by the env var — no workspace
  // opt-in gate (that was the legacy Supabase cutover mechanism; with our own
  // backend, ALL workspaces are ours).
  if (!isPhase2EnabledForCurrentWorkspace()) return '';
  // Production-build fallback. CRA bakes NODE_ENV='production' into
  // the bundle at build time, so this branch only fires in real
  // deployed builds — never in `npm start` (development) or jest
  // (test). Keeping it dev/test-quiet means a forgotten fetch mock
  // can't accidentally hit the live VPS from a unit test.
  if (process.env.NODE_ENV === 'production') return PROD_API_BASE_URL;
  return '';
}

/** Is an api-only call possible at all? URL only — the workspace gate is not asked. */
export function apiOnlyConfigured(): boolean {
  return resolveApiUrl().length > 0;
}

/** True if the backend is configured. Doesn't actually probe — see `pingApi()`. */
export function apiConfigured(): boolean {
  return getApiBase().length > 0;
}

/**
 * Public twin of the private `getApiBase()` — for transports that build their
 * own URL (e.g. the realtime SSE client needs `${base}/events?…` with the
 * access token as a query param because EventSource cannot set headers).
 */
export function getApiBaseUrl(): string {
  return getApiBase();
}

/**
 * Auth-path base URL. The LOGIN page has no workspace yet, so the phase-2
 * per-workspace gate in getApiBase() would (correctly) block it. Auth is
 * workspace-independent: it only needs the backend URL, not a workspace
 * opt-in. Returns '' if the backend isn't configured at all.
 */
export function getAuthBaseUrl(): string {
  const fromEnv = (process.env.REACT_APP_API_BASE_URL ?? '').replace(/\/$/, '');
  if (fromEnv) return fromEnv;
  if (process.env.NODE_ENV === 'production') return PROD_API_BASE_URL;
  return '';
}

/**
 * True when the backend is configured for AUTH (login/register/refresh) —
 * independent of the workspace opt-in gate.
 */
export function authConfigured(): boolean {
  return getAuthBaseUrl().length > 0;
}


interface ApiSuccess<T> {
  ok: true;
  status: number;
  data: T;
}

interface ApiFailure {
  ok: false;
  status: number;       // 0 if the request never made it (network error)
  error: string;
  /** A machine-readable code from the body's `code`, when the backend sent one
   *  (e.g. /data's `PGRST116` for `.single()` with no row). Absent otherwise. */
  code?: string;
}

export type ApiResult<T> = ApiSuccess<T> | ApiFailure;

// ─── Sign-in renewal (2026-10-24) ───────────────────────────────────────────
//
// ⛔ THE ACCESS PASS LIVES 60 MINUTES, AND UNTIL THIS SECTION NOTHING RENEWED IT.
// Supabase's client renewed its session by itself. The native sign-in that replaced
// it on switch day (2026-10-23) stored a refresh token and never used one. Measured
// on production by the switch-day session on 2026-10-24: an hour after each sign-in,
// 128 of 129 `/api/v1` calls from the browser were 401, and the browser made no
// `/auth/refresh` call in two days. The profile could not load, the sidebar fell back
// to "User", and the admin menus went with the role.
//
// So a request refused with 401 renews the pass ONCE and is retried ONCE. Three rules
// keep that from becoming a storm, which matters because nginx rate-limits
// `/api/v1/auth/` at 5 a minute (burst 10) and a storm would turn the renewal itself
// into 429s:
//
//   1. ONE renewal in flight, shared. Thirty parallel 401s wait on the same
//      `/auth/refresh` call; none starts its own.
//   2. A 401 for a token that has ALREADY been replaced is not a reason to renew.
//      The call goes again with the new token. That is the late arrival from the
//      same parallel batch, or another tab that renewed first.
//   3. A just-renewed token that is still refused is not a stale-token problem, so
//      no second renewal starts within RENEW_MIN_INTERVAL_MS. Without this, a route
//      that answers 401 for its own reasons would renew on every call.
//
// When the refresh token itself is dead (the server answers 400/401/403/422), the
// session is cleared, a sentence is left for the log-in page, and the listeners are
// told. AuthContext then signs out, and the router sends the person to /login.
// ⚠️ A network failure, a 429 or a 5xx is NOT a dead pass. Nobody is signed out for
// those; the next 401 after RENEW_MIN_INTERVAL_MS tries again.
//
// The session is read from localStorage directly, as `authHeader` always did, because
// authNative.ts imports this module and importing it back would be a cycle.

const NATIVE_SESSION_KEY = 'pulsehq_native_session';

/** sessionStorage: the sentence the log-in page shows after an automatic sign-out. */
const SIGNED_OUT_REASON_KEY = 'pulsehq_signed_out_reason';

export const SESSION_EXPIRED_MESSAGE = 'Your sign-in has expired. Please log in again.';

const RENEW_MIN_INTERVAL_MS = 15_000;

/** At boot, a pass with less than this left is renewed before the first data call. */
const BOOT_EXPIRY_MARGIN_S = 30;

/** What the refresh endpoint answers for a refresh token that will never work again. */
const DEAD_REFRESH_STATUSES = new Set([400, 401, 403, 422]);

interface StoredSession {
  access_token?: string;
  refresh_token?: string;
  user?: unknown;
}

function readStoredSession(): StoredSession | null {
  const raw = lsGet(NATIVE_SESSION_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as StoredSession | null;
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * The credential exchanges themselves. A 401 from one of these means a wrong password
 * or a dead refresh token, never a stale access token, so it is never renewed and
 * retried. Retrying `/auth/refresh` on its own 401 would also recurse.
 */
function isCredentialExchange(path: string): boolean {
  return /^\/auth\/(login|refresh|register)(?:[/?]|$)/.test(path);
}

/** `renewed`: a new pass is stored. `expired`: signed out. `failed`: try again later. */
export type SessionRenewal = 'renewed' | 'expired' | 'failed';

let renewalInFlight: Promise<SessionRenewal> | null = null;
let lastRenewalAt = 0;
let bootRenewal: Promise<SessionRenewal | 'current'> | null = null;
const expiryListeners = new Set<() => void>();

/** Called when the refresh token is dead and the session has been cleared. */
export function onSessionExpired(listener: () => void): () => void {
  expiryListeners.add(listener);
  return () => {
    expiryListeners.delete(listener);
  };
}

/** The log-in page's sentence after an automatic sign-out, if there was one. */
export function peekSignedOutReason(): string | null {
  try {
    return typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(SIGNED_OUT_REASON_KEY) : null;
  } catch {
    return null;
  }
}

export function clearSignedOutReason(): void {
  try {
    if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem(SIGNED_OUT_REASON_KEY);
  } catch {
    /* noop */
  }
}

/**
 * Clear the session whose refresh token was refused, and tell the listeners.
 * Only if it is still the stored one: a sign-in that landed while the refusal was in
 * flight is a new session and must not be thrown away with the old one.
 */
function expireSession(rejectedRefreshToken: string | undefined): void {
  if (readStoredSession()?.refresh_token !== rejectedRefreshToken) return;
  lsRemove(NATIVE_SESSION_KEY);
  try {
    if (typeof sessionStorage !== 'undefined') sessionStorage.setItem(SIGNED_OUT_REASON_KEY, SESSION_EXPIRED_MESSAGE);
  } catch {
    /* noop: the sign-out still happens, only the sentence is lost */
  }
  expiryListeners.forEach((listener) => {
    try {
      listener();
    } catch {
      /* one listener failing must not stop the others */
    }
  });
}

async function runRenewal(): Promise<SessionRenewal> {
  const current = readStoredSession();
  if (!current) return 'expired';
  const refreshToken = current.refresh_token;
  if (!refreshToken) {
    expireSession(refreshToken);
    return 'expired';
  }
  lastRenewalAt = Date.now();
  const res = await request<{ access_token?: unknown; refresh_token?: unknown }>(
    'POST',
    '/auth/refresh',
    { refresh_token: refreshToken },
  );
  const now = readStoredSession();
  // Signed out while the renewal was in flight: never bring the session back.
  if (!now) return 'expired';
  // Replaced meanwhile (a new sign-in, another tab's renewal): that one stands.
  if (now.refresh_token !== refreshToken) return 'renewed';
  if (res.ok) {
    const accessToken = res.data?.access_token;
    const nextRefresh = res.data?.refresh_token;
    if (typeof accessToken !== 'string' || !accessToken || typeof nextRefresh !== 'string' || !nextRefresh) {
      return 'failed';
    }
    lsSet(NATIVE_SESSION_KEY, JSON.stringify({ ...now, access_token: accessToken, refresh_token: nextRefresh }));
    return 'renewed';
  }
  if (DEAD_REFRESH_STATUSES.has(res.status)) {
    expireSession(refreshToken);
    return 'expired';
  }
  return 'failed';
}

/**
 * Exchange the stored refresh token for a new pair. Every caller shares the one
 * renewal in flight, so this is safe to call from any number of places at once.
 */
export function renewSession(): Promise<SessionRenewal> {
  if (!renewalInFlight) {
    renewalInFlight = runRenewal()
      .catch((): SessionRenewal => 'failed')
      .finally(() => {
        renewalInFlight = null;
      });
  }
  return renewalInFlight;
}

/** The `exp` claim of a JWT, in seconds, or null when it cannot be read. */
function jwtExpiry(token: string): number | null {
  const payload = token.split('.')[1];
  if (!payload) return null;
  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    const exp = (JSON.parse(atob(padded)) as { exp?: unknown }).exp;
    return typeof exp === 'number' ? exp : null;
  } catch {
    return null;
  }
}

/**
 * Once per page load: if the stored pass has already run out, renew it before the
 * first data call instead of letting every opening call collect a 401. `request()`
 * starts it on the first call, whoever makes it, and every call waits for it.
 *
 * Once, and not before every call, on purpose: a browser clock that runs ahead of the
 * server's would make every fresh pass look expired, and checking on every call would
 * then renew on every call.
 */
export function renewExpiredSessionAtBoot(): Promise<SessionRenewal | 'current'> {
  if (!bootRenewal) {
    const stored = readStoredSession();
    const exp = stored?.access_token ? jwtExpiry(stored.access_token) : null;
    const expired = exp !== null && exp - BOOT_EXPIRY_MARGIN_S <= Date.now() / 1000;
    bootRenewal = expired && stored?.refresh_token ? renewSession() : Promise.resolve('current');
  }
  return bootRenewal;
}

/** After a 401 sent with `sentToken`: should the call go once more? */
async function renewalAllowsRetry(sentToken: string): Promise<boolean> {
  const stored = readStoredSession()?.access_token;
  if (!stored) return false;
  if (stored !== sentToken) return true;
  if (!renewalInFlight && Date.now() - lastRenewalAt < RENEW_MIN_INTERVAL_MS) return false;
  return (await renewSession()) === 'renewed';
}

/** For tests only: forget the renewal state, as a fresh page load would. */
export const __testing = {
  reset(): void {
    renewalInFlight = null;
    lastRenewalAt = 0;
    bootRenewal = null;
    expiryListeners.clear();
  },
};

interface Bearer {
  token: string | null;
  /** True when the token is our own native session's, which is the only kind renewed here. */
  native: boolean;
}

async function currentBearer(): Promise<Bearer> {
  // PRIMARY (2026-08-14): our OWN native-auth token — the session seeded by
  // AuthContext into localStorage under pulsehq_native_session. This is what
  // the /data endpoint authenticates. Read directly here to avoid a circular
  // import with authNative.ts (which imports this module).
  const native = readStoredSession()?.access_token;
  if (native) return { token: native, native: true };
  // LEGACY: fall back to the Supabase session (deployed/cousin path).
  // eslint-disable-next-line no-restricted-syntax -- auth session read, not a data query; no swallowable fetch error
  const { data } = await supabase.auth.getSession();
  return { token: data.session?.access_token ?? null, native: false };
}

async function request<T>(
  method: 'GET' | 'POST' | 'DELETE' | 'PATCH' | 'PUT',
  path: string,
  body?: unknown,
  { ungated = false }: { ungated?: boolean } = {},
): Promise<ApiResult<T>> {
  // Three ways to reach a base URL, and each exists for a different reason:
  //
  //   ungated      main's API-only routes (the mail screens), which have no
  //                Supabase path at all, so the workspace gate's "off" position
  //                would mean "the screen silently does nothing".
  //   isAuthPath   login/register/refresh/password run BEFORE a workspace
  //                exists. So does the self-profile read — /data/profiles is
  //                what SETS the workspace during login, a chicken-and-egg the
  //                gate would deadlock.
  //   otherwise    the normal gated path.
  const isAuthPath = path.startsWith('/auth/') || path.startsWith('/data/profiles');
  const base = ungated
    ? resolveApiUrl()
    : isAuthPath
      ? getAuthBaseUrl()
      : getApiBase();
  if (!base) {
    return { ok: false, status: 0, error: 'Backend not configured' };
  }
  const url = `${base}${path}`;
  const exchange = isCredentialExchange(path);
  const send = async (): Promise<{ resp: Response; bearer: Bearer }> => {
    const bearer = await currentBearer();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(bearer.token ? { Authorization: `Bearer ${bearer.token}` } : {}),
    };
    const resp = await fetch(url, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    return { resp, bearer };
  };
  try {
    if (!exchange) {
      // A pass that ran out before this page loaded is renewed before the first call,
      // and a call made while a renewal is in flight waits for its result rather than
      // spending a 401 on the old pass.
      if (!bootRenewal) void renewExpiredSessionAtBoot();
      if (renewalInFlight) await renewalInFlight;
    }
    let sent = await send();
    if (
      sent.resp.status === 401 &&
      !exchange &&
      sent.bearer.native &&
      sent.bearer.token &&
      (await renewalAllowsRetry(sent.bearer.token))
    ) {
      // Once. A second 401 is returned to the caller as it is.
      sent = await send();
    }
    const resp = sent.resp;
    if (resp.status === 204) {
      return { ok: true, status: 204, data: undefined as unknown as T };
    }
    let parsed: unknown = undefined;
    const text = await resp.text();
    if (text) {
      try { parsed = JSON.parse(text); } catch { parsed = text; }
    }
    if (!resp.ok) {
      const detail =
        parsed && typeof parsed === 'object' && 'detail' in parsed
          ? String((parsed as { detail: unknown }).detail)
          : (typeof parsed === 'string' ? parsed : `HTTP ${resp.status}`);
      const code =
        parsed && typeof parsed === 'object' && typeof (parsed as { code?: unknown }).code === 'string'
          ? (parsed as { code: string }).code
          : undefined;
      return code
        ? { ok: false, status: resp.status, error: detail, code }
        : { ok: false, status: resp.status, error: detail };
    }
    return { ok: true, status: resp.status, data: parsed as T };
  } catch (err) {
    // fetch throws on network/CORS errors. Treat as soft failure so
    // callers can fall back without an unhandled rejection.
    return {
      ok: false,
      status: 0,
      error: err instanceof Error ? err.message : 'Network error',
    };
  }
}

export const api = {
  get:    <T>(path: string)               => request<T>('GET',    path),
  post:   <T>(path: string, body?: unknown) => request<T>('POST',   path, body),
  patch:  <T>(path: string, body?: unknown) => request<T>('PATCH',  path, body),
  put:    <T>(path: string, body?: unknown) => request<T>('PUT',    path, body),
  delete: <T>(path: string)               => request<T>('DELETE', path),
};

/**
 * The same transport, resolving its URL WITHOUT the per-workspace gate.
 *
 * ⚠️ **NOT a general-purpose escape hatch.** See `resolveApiUrl()` above for why it
 * exists and why the gate is still in `getApiBase()` for everything else. Reach for
 * this only from `apiOnlyRead` / `apiOnlyWrite`, whose contracts make "there is no
 * Supabase answer" explicit — a bare `apiOnly.post()` in a component is a bridged
 * call that forgot its fallback, which is the thing `apiBridge.ts` exists to prevent.
 */
export const apiOnly = {
  get:    <T>(path: string)                 => request<T>('GET',    path, undefined, { ungated: true }),
  post:   <T>(path: string, body?: unknown) => request<T>('POST',   path, body,      { ungated: true }),
  patch:  <T>(path: string, body?: unknown) => request<T>('PATCH',  path, body,      { ungated: true }),
  delete: <T>(path: string)                 => request<T>('DELETE', path, undefined, { ungated: true }),
};

/**
 * Cheap reachability probe. Calls `/health/live` (no auth required).
 * Use this once at app boot to decide whether to enable the backend
 * code paths for this session.
 *
 * Cached for 30 s to avoid hammering the health endpoint on every nav.
 */
let lastProbe: { ts: number; ok: boolean } | null = null;
const PROBE_TTL_MS = 30_000;

export async function pingApi(): Promise<boolean> {
  const base = getApiBase();
  if (!base) return false;
  if (lastProbe && Date.now() - lastProbe.ts < PROBE_TTL_MS) {
    return lastProbe.ok;
  }
  try {
    const resp = await fetch(`${base}/health/live`, {
      method: 'GET',
      // Short timeout so we fall back fast if the backend is down.
      signal: AbortSignal.timeout(2000),
    });
    const ok = resp.ok;
    lastProbe = { ts: Date.now(), ok };
    return ok;
  } catch {
    lastProbe = { ts: Date.now(), ok: false };
    return false;
  }
}
